import axios from 'axios'
import { AlertCircle, Clock, DollarSign, TrendingUp, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8888'

interface UsageStats {
  tier: string
  tokens_used: number
  tokens_remaining: number
  tokens_limit: number | null
  total_cost: number
  price_per_1k_tokens: number
  can_use: boolean
}

interface HistoryItem {
  provider: string
  model: string
  tokens_used: number
  cost: number
  created_at: string
}

export default function LLMSharedPool() {
  const [prompt, setPrompt] = useState('')
  const [provider, setProvider] = useState('gemini')
  const [maxTokens, setMaxTokens] = useState(1000)
  const [temperature, setTemperature] = useState(0.7)
  const [response, setResponse] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [usage, setUsage] = useState<UsageStats | null>(null)
  const [history, setHistory] = useState<HistoryItem[]>([])

  useEffect(() => {
    void loadUsage()
    void loadHistory()
  }, [])

  const loadUsage = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await axios.get(`${API_URL}/api/llm/shared-pool/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setUsage(res.data as UsageStats)
    } catch (err: any) {
      console.error('Usage load failed:', err)
    }
  }

  const loadHistory = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await axios.get(`${API_URL}/api/llm/shared-pool/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setHistory((res.data.history || []) as HistoryItem[])
    } catch (err: any) {
      console.error('History load failed:', err)
    }
  }

  const handleInference = async () => {
    if (!prompt.trim()) {
      setError('Prompt gerekli')
      return
    }

    if (!usage?.can_use) {
      setError('Aylık token limitiniz doldu. Pro tier\'a yükseltin.')
      return
    }

    setLoading(true)
    setError('')
    setResponse('')

    try {
      const token = localStorage.getItem('token')
      const res = await axios.post(
        `${API_URL}/api/llm/inference/shared-pool`,
        {
          prompt,
          provider,
          max_tokens: maxTokens,
          temperature
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      setResponse(res.data.response as string)

      // Reload usage & history
      await loadUsage()
      await loadHistory()
    } catch (err: any) {
      setError((err.response?.data?.detail as string) || 'Inference başarısız')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">AiCloud Shared Pool</h1>
        <p className="text-gray-400">
          RunAI&apos;nin API key&apos;lerini kullan - Kullandığın kadar öde
        </p>
      </div>

      {/* Usage Stats */}
      {usage && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Zap size={20} className="text-blue-400" />
              <span className="text-sm text-gray-400">Tier</span>
            </div>
            <div className="text-2xl font-bold capitalize">{usage.tier}</div>
          </div>

          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={20} className="text-green-400" />
              <span className="text-sm text-gray-400">Kullanılan</span>
            </div>
            <div className="text-2xl font-bold">
              {usage.tokens_used.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500">
              / {usage.tokens_limit ? usage.tokens_limit.toLocaleString() : '∞'} tokens
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={20} className="text-yellow-400" />
              <span className="text-sm text-gray-400">Kalan</span>
            </div>
            <div className="text-2xl font-bold">
              {usage.tokens_remaining.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500">tokens</div>
          </div>

          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign size={20} className="text-purple-400" />
              <span className="text-sm text-gray-400">Toplam Maliyet</span>
            </div>
            <div className="text-2xl font-bold">
              ${usage.total_cost.toFixed(4)}
            </div>
            <div className="text-xs text-gray-500">
              ${usage.price_per_1k_tokens}/1k tokens
            </div>
          </div>
        </div>
      )}

      {/* Inference Form */}
      <div className="bg-gray-800 rounded-lg p-6 mb-8">
        <h2 className="text-xl font-bold mb-4">LLM Inference</h2>

        {/* Provider Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Provider</label>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
          >
            <option value="gemini">Google Gemini</option>
            <option value="mistral">Mistral AI</option>
            <option value="groq">Groq LPU</option>
          </select>
        </div>

        {/* Prompt */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Prompt</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Mesajınızı yazın..."
            rows={4}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
          />
        </div>

        {/* Advanced Settings */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Max Tokens: {maxTokens}
            </label>
            <input
              type="range"
              min="100"
              max="4000"
              step="100"
              value={maxTokens}
              onChange={(e) => setMaxTokens(Number(e.target.value))}
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Temperature: {temperature}
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={temperature}
              onChange={(e) => setTemperature(Number(e.target.value))}
              className="w-full"
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-900/20 border border-red-500 rounded flex items-start gap-2">
            <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
            <span className="text-red-400 text-sm">{error}</span>
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={() => void handleInference()}
          disabled={loading || !usage?.can_use}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded transition-colors"
        >
          {loading ? 'İşleniyor...' : 'Gönder'}
        </button>
      </div>

      {/* Response */}
      {response && (
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold mb-4">Yanıt</h2>
          <div className="bg-gray-900 rounded p-4 whitespace-pre-wrap">
            {response}
          </div>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">Son Kullanımlar</h2>
          <div className="space-y-3">
            {history.map((item, idx) => (
              <div key={idx} className="bg-gray-900 rounded p-3 flex items-center justify-between">
                <div>
                  <div className="font-medium capitalize">{item.provider}</div>
                  <div className="text-sm text-gray-400">{item.model}</div>
                  <div className="text-xs text-gray-500">
                    {new Date(item.created_at).toLocaleString('tr-TR')}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium">
                    {item.tokens_used.toLocaleString()} tokens
                  </div>
                  <div className="text-xs text-gray-400">
                    ${item.cost.toFixed(4)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
